import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import PageShell from "@/components/PageShell";
import PageHeader from "@/components/PageHeader";
import SiteFooter from "@/components/SiteFooter";
import { COMPANY } from "@/data/site";
import { PRODUCTS } from "@/data/products";

const initial = { name: "", company: "", email: "", phone: "", product: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm({ ...form, [k]: v });
    if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Tell us a bit more (min 10 chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (action) => async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error("Please review the highlighted fields.");
      return;
    }
    
    setSubmitting(true);
    const toastId = toast.loading("Sending your message...");
    
    try {
      const response = await fetch(
        "https://lightseagreen-moose-831884.hostingersite.com/wordpress/wp-json/custom/v1/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            name: form.name,
            company_name: form.company,
            email: form.email,
            phone_number: form.phone,
            message: form.product ? `Product Required: ${form.product}\n\n${form.message}` : form.message
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send message. Please try again later.");
      }

      toast.success("Message received. We'll be in touch shortly.", { id: toastId });
      setForm(initial);
      setErrors({});
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again.", { id: toastId });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <PageHeader
        overline="Contact"
        title={<>Tell us what you&apos;re<br />planning to print.</>}
        subtitle="Send a message or request a custom quote — we typically reply within a few hours."
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "Contact" }]}
      />

      <section className="bg-white dark:bg-[#0B1120] py-20 md:py-28 transition-colors">
        <div className="pz-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Info */}
          <div className="lg:col-span-5">
            <div className="space-y-5">
              {[
                // { icon: Phone, label: "Call us", value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s/g, "")}` },
                // { icon: Mail, label: "Email us", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
                // { icon: MapPin, label: "Visit us", value: "Deira, Dubai - UAE" },
                {
  icon: Phone,
  label: "Call us",
  value: COMPANY.phone,
  href: `tel:${COMPANY.phone.replace(/\s/g, "")}`,
},
{
  icon: Mail,
  label: "Email us",
  value: COMPANY.email,
  href: `https://mail.google.com/mail/?view=cm&fs=1&to=${COMPANY.email}`,
},
{
  icon: MapPin,
  label: "Visit us",
  value: "707, BMI building, Bank Street, Bur Dubai, Dubai-UAE",
  href: "https://maps.google.com/?q=Deira,Dubai,UAE",
},
                // { icon: Clock, label: "Working hours", value: "Available 24/7" },
              ].map((row) => {
                const Icon = row.icon;
                const inner = (
                  <>
                    <div className="w-11 h-11 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/15 group-hover:border-[#0F172A] dark:group-hover:border-[#F59E0B] grid place-items-center transition-colors">
                      <Icon className="w-5 h-5 text-[#0F172A] dark:text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-white/55 font-semibold">{row.label}</div>
                      <div className="font-display text-[#0F172A] dark:text-white text-base font-medium mt-1">{row.value}</div>
                    </div>
                  </>
                );
                return row.href ? (
                  <a key={row.label} href={row.href} data-testid={`contact-info-${row.label.toLowerCase().replace(/\s/g, "-")}`} className="group flex items-center gap-4 hover:translate-x-1 transition-transform">{inner}</a>
                ) : (
                  <div key={row.label} className="group flex items-center gap-4">{inner}</div>
                );
              })}
            </div>

            <div className="mt-10 rounded-3xl overflow-hidden border border-slate-200 dark:border-white/10">
              <iframe
                title="Printzee Dubai location"
                 src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d57728.74162553496!2d55.3256035!3d25.26902615!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f434f37cdae93%3A0xde756363a1b78491!2sDeira%20-%20Dubai!5e0!3m2!1sen!2sae!4v1781274538720!5m2!1sen!2sae"

                width="100%"
                height="280"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0 }}
              />
            </div>
          </div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            onSubmit={submit("message")}
            data-testid="contact-page-form"
            className="lg:col-span-7 bg-white dark:bg-white/[0.03] rounded-3xl p-7 sm:p-10 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.18)] border border-slate-100 dark:border-white/10"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name" error={errors.name}>
                <input data-testid="contact-page-name" value={form.name} onChange={update("name")} placeholder="Your full name" className={inputCls(errors.name)} />
              </Field>
              <Field label="Company Name">
                <input data-testid="contact-page-company" value={form.company} onChange={update("company")} placeholder="Acme Holdings" className={inputCls()} />
              </Field>
              <Field label="Email" error={errors.email}>
                <input data-testid="contact-page-email" type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className={inputCls(errors.email)} />
              </Field>
              <Field label="Phone Number" error={errors.phone}>
                <input data-testid="contact-page-phone" value={form.phone} onChange={update("phone")} placeholder="+971 50 000 0000" className={inputCls(errors.phone)} />
              </Field>
            
              <div className="sm:col-span-2">
                <Field label="Message" error={errors.message}>
                  <textarea data-testid="contact-page-message" value={form.message} onChange={update("message")} placeholder="Tell us about your project, timeline, quantities..." rows={5} className={`${inputCls(errors.message)} resize-none py-3`} />
                </Field>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-page-submit"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full pz-btn-gold text-sm disabled:opacity-50"
              >
                {submitting ? "Sending..." : "Send Message"} <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.form>
        </div>
      </section>

      <SiteFooter />
    </PageShell>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-white/55 font-semibold mb-2">{label}</label>
      {children}
      {error && <div className="mt-1.5 text-xs text-red-500">{error}</div>}
    </div>
  );
}

function inputCls(error) {
  return `w-full h-12 rounded-xl border ${error ? "border-red-400" : "border-slate-200 dark:border-white/15"} bg-white dark:bg-white/5 px-4 text-[15px] text-[#0F172A] dark:text-white placeholder:text-slate-400 dark:placeholder:text-white/35 outline-none focus:border-[#0F172A] dark:focus:border-[#F59E0B] focus:ring-2 focus:ring-[#2563EB]/20 transition-all`;
}
